import { Controller } from "@hotwired/stimulus"

// Shared connectivity detector for PWA apps.
//
// Dual detection:
//   1. window 'offline'/'online' events -- fires instantly on NIC change
//   2. Polls a server-side ping endpoint every 10s -- catches "LAN but no internet"
//
// Respects the {prefix}:offline_mode preference:
//   auto         -- pixel polling active (default)
//   online_only  -- polling disabled; status permanently online; badge hidden
//   manual       -- polling disabled; manualToggle button shown; user controls status
//
// Stores result in localStorage under "{prefix}:online" (string "true"/"false").
// Dispatches "{prefix}:online" and "{prefix}:offline" events on window.
//
// Card #3862 -- a browser that reports no connection at all
// (navigator.onLine === false: airplane mode, radios off) is offline in Auto
// AND Manual, checked at connect and on the "offline" event, so the app no
// longer waits for a ping (Auto) or a tap (Manual) to find out. Online-only
// keeps its promise of "always online". Why the app went offline is kept in
// "{prefix}:offline_source" (device | ping | manual) and logged with every
// change: in Manual, a device-caused offline comes back online by itself once
// the browser reports a connection and a ping confirms it, but a toggle-caused
// one stays offline until the tasker turns it back on.
//
// Stimulus values:
//   prefix         -- localStorage/event namespace (e.g. "field", "chat")
//   pingUrl        -- server endpoint to poll
//   interval       -- polling interval in ms (default 10000)
//   swSyncEnabled  -- whether to sync offline state to service worker (default false)

export default class extends Controller {
  static targets = ["badge", "manualToggle", "offlineBanner", "themeColor"]

  static values = {
    prefix:        String,
    pingUrl:       String,
    interval:      { type: Number, default: 10000 },
    swSyncEnabled: { type: Boolean, default: false },
    currentMode:   String
  }

  connect() {
    // Card #4924 follow-up -- the server is the source of truth for mode on
    // every fresh page load. localStorage is otherwise only ever written by
    // offline_mode_controller.js on the Settings page, so a tasker who never
    // visits Settings would keep a stale/default "auto" value forever, even
    // after their field_offline_mode column is backfilled to "manual". Must
    // run before anything below reads _mode().
    if (this.hasCurrentModeValue && this.currentModeValue) {
      localStorage.setItem(`${this.prefixValue}:offline_mode`, this.currentModeValue)
    }

    this.online = localStorage.getItem(`${this.prefixValue}:online`) !== "false"
    if (this._mode() !== "online_only" && navigator.onLine === false) this._setOnline(false, "device")
    this._applyMode()

    this._modeChangedHandler = (e) => this._onModeChanged(e.detail.mode)
    window.addEventListener(`${this.prefixValue}:offline-mode-changed`, this._modeChangedHandler)

    // Instant detection when the network interface drops or comes back.
    // On 'online' we verify with a real ping rather than trusting the browser event
    // (LAN-connected-but-no-internet would otherwise report as online).
    // Online-only promises "always online": neither browser event moves it.
    this._offlineHandler = () => { if (this._mode() !== "online_only") this._setOnline(false, "device") }
    this._onlineHandler  = () => { if (this._mode() !== "online_only") this._onDeviceOnline() }
    window.addEventListener("offline", this._offlineHandler)
    window.addEventListener("online",  this._onlineHandler)

    if (this.swSyncEnabledValue) {
      this._syncSwOfflineMode()
      this._swControllerChangeHandler = () => this._syncSwOfflineMode()
      navigator.serviceWorker?.addEventListener("controllerchange", this._swControllerChangeHandler)
      // Card #3864 -- a service worker that restarted while the page was in the
      // background forgot the client's online state; re-post it on return.
      this._visibilityHandler = () => { if (document.visibilityState === "visible") this._syncSwOfflineMode() }
      document.addEventListener("visibilitychange", this._visibilityHandler)
    }
  }

  disconnect() {
    clearInterval(this._intervalId)
    window.removeEventListener(`${this.prefixValue}:offline-mode-changed`, this._modeChangedHandler)
    window.removeEventListener("offline", this._offlineHandler)
    window.removeEventListener("online",  this._onlineHandler)

    if (this.swSyncEnabledValue) {
      navigator.serviceWorker?.removeEventListener("controllerchange", this._swControllerChangeHandler)
      document.removeEventListener("visibilitychange", this._visibilityHandler)
    }
  }

  // Called by the manual toggle button.
  // Toggling offline is immediate. Toggling online requires a real ping first --
  // clicking "Check connection" when you have no network should not hide the banner.
  //
  // When going online with SW sync: lift the SW block BEFORE pinging. Without this
  // the SW would intercept the ping itself and we could never get back online.
  toggleOnline() {
    if (this.online) {
      this._setOnline(false, "manual")
    } else {
      if (this.swSyncEnabledValue) this._notifySw(false)
      this._poll("manual")
    }
  }

  // -- private --

  _mode() {
    return localStorage.getItem(`${this.prefixValue}:offline_mode`) || "auto"
  }

  _offlineSource() {
    return localStorage.getItem(`${this.prefixValue}:offline_source`)
  }

  // The browser reports a connection again. Auto confirms with a ping, as
  // before. Manual (card #3862) only does so when the device caused the
  // offline -- a tasker who turned the toggle off stays offline.
  _onDeviceOnline() {
    const mode = this._mode()
    if (mode === "manual") {
      if (this.online || this._offlineSource() === "manual") return
      if (this.swSyncEnabledValue) this._notifySw(false)
    }
    this._poll("device")
  }

  _applyMode() {
    const mode = this._mode()
    clearInterval(this._intervalId)

    if (mode === "online_only") {
      this._setOnline(true)
      this._updateBadge()
      this._updateManualToggle(false)
      return
    }

    if (mode === "manual") {
      this._updateBadge()
      this._updateManualToggle(true)
      // No polling in Manual, so nothing else re-posts the client's state;
      // the service worker ages entries out after 30s (card #3864).
      if (this.swSyncEnabledValue) {
        this._intervalId = setInterval(() => this._postClientState(), this.intervalValue)
      }
      // Device-caused offline from an earlier page, and the browser now has a
      // connection: confirm and recover (card #3862).
      if (!this.online && this._offlineSource() !== "manual" && navigator.onLine !== false) this._onDeviceOnline()
      return
    }

    // auto -- draw the stored state straight away (card #3862: Turbo replaces
    // <body> on every visit, so the pill and strip used to vanish until the
    // first ping settled, up to its 8s timeout on a hanging network), then
    // poll immediately and on interval.
    this._updateBadge()
    this._poll()
    this._intervalId = setInterval(() => this._poll(), this.intervalValue)
    this._updateManualToggle(false)
  }

  _onModeChanged(mode) {
    this._log(`Offline mode changed to: ${mode}`)
    this._applyMode()
  }

  // source: what asked for this check -- "ping" for the regular poll, "device"
  // after the browser's online event, "manual" after the toggle -- recorded
  // when it brings the app back online.
  async _poll(source = "ping") {
    const startedAt = performance.now()
    try {
      const sep = this.pingUrlValue.includes("?") ? "&" : "?"
      const res = await fetch(`${this.pingUrlValue}${sep}t=${Date.now()}`, {
        method: "GET",
        cache:  "no-store",
        signal: AbortSignal.timeout(8000)
      })
      this._recordPingSample(Math.round(performance.now() - startedAt), res.ok ? "ok" : "server_error")
      // Card #4859 pt 2b -- carried as a response header rather than a body
      // change, since this endpoint's body is a fixed 1x1 PNG shared by
      // every PWA this controller serves (chat included); only Field's own
      // ping (Field::PingController) ever sets it. Dispatched regardless of
      // prefix -- harmless where nothing listens -- so field_update_state.js
      // stays the one place that decides what to do with it.
      if (res.ok) {
        window.dispatchEvent(new CustomEvent(`${this.prefixValue}:force-update-ping`, {
          detail: { pending: res.headers.get("X-Field-Force-Update") === "1" }
        }))
      }
      // A tasker who switched Manual offline while this ping was in flight
      // stays offline: only the toggle's own ping may bring them back.
      if (res.ok && source !== "manual" && this._mode() === "manual" && this._offlineSource() === "manual") return
      this._setOnline(res.ok, res.ok ? source : "ping")
    } catch {
      this._recordPingSample(Math.round(performance.now() - startedAt), "network_error")
      this._setOnline(false, "ping")
    }
  }

  // source: device | ping | manual (card #3862). Stored while offline, and
  // logged with every change so an investigation can see why.
  //
  // "{prefix}:offline_since" (epoch ms) is kept alongside, so the online
  // event can say how long the app was offline (detail.offlineMs, null if
  // unknown) -- the sync controller only clears the photo retry wait after a
  // real outage, not a signal flap (card #3844).
  _setOnline(nowOnline, source = "ping") {
    const changed = nowOnline !== this.online
    this.online   = nowOnline
    localStorage.setItem(`${this.prefixValue}:online`, nowOnline ? "true" : "false")
    if (!nowOnline && changed) {
      localStorage.setItem(`${this.prefixValue}:offline_source`, source)
      localStorage.setItem(`${this.prefixValue}:offline_since`, String(Date.now()))
    }
    let offlineMs = null
    if (nowOnline) {
      const since = Number(localStorage.getItem(`${this.prefixValue}:offline_since`))
      if (changed && since > 0) offlineMs = Math.max(0, Date.now() - since)
      localStorage.removeItem(`${this.prefixValue}:offline_source`)
      localStorage.removeItem(`${this.prefixValue}:offline_since`)
    }

    if (changed) {
      this._log(nowOnline ? "Back online" : "Went offline", nowOnline ? { source, offlineMs } : { source })
      window.dispatchEvent(new CustomEvent(
        nowOnline ? `${this.prefixValue}:online` : `${this.prefixValue}:offline`,
        { bubbles: true, detail: nowOnline ? { source, offlineMs } : { source } }
      ))
    }

    this._updateBadge()
    this._updateManualToggle(localStorage.getItem(`${this.prefixValue}:offline_mode`) === "manual")
    if (this.swSyncEnabledValue) this._syncSwOfflineMode()
  }

  // Sends the current forced-offline state to the SW.
  // forcedOffline = true only when mode is "manual" AND we are currently offline.
  // In auto/online_only mode the SW never blocks requests.
  _syncSwOfflineMode() {
    const mode   = localStorage.getItem(`${this.prefixValue}:offline_mode`) || "auto"
    const forced = mode === "manual" && !this.online
    this._notifySw(forced)
  }

  _notifySw(forced) {
    const sw = navigator.serviceWorker?.controller
    sw?.postMessage({
      type: 'SYNC_OFFLINE_MODE',
      offline: forced
    })
    this._postClientState()
  }

  // Card #3864 -- the client's real online state, whatever the mode, so the
  // service worker can serve field navigations cache-first while it is
  // offline. The worker stamps it and ignores it after 30s.
  _postClientState() {
    navigator.serviceWorker?.controller?.postMessage({ type: 'CLIENT_ONLINE_STATE', online: this.online !== false })
  }

  _updateBadge() {
    const mode    = localStorage.getItem(`${this.prefixValue}:offline_mode`) || "auto"
    const offline = !(mode === "online_only" || this.online)

    if (this.hasBadgeTarget) {
      this.badgeTarget.classList.toggle("hidden", !offline || mode === "manual")
    }

    if (this.hasOfflineBannerTarget) {
      this.offlineBannerTarget.classList.toggle("hidden", !offline)
    }

    if (this.hasThemeColorTarget) {
      this.themeColorTarget.content = offline ? "#f59e0b" : "#003d68"
    }
  }

  _updateManualToggle(show) {
    if (!this.hasManualToggleTarget) return
    if (show) {
      this.manualToggleTarget.classList.remove("hidden")
      this.manualToggleTarget.setAttribute("aria-checked", this.online ? "true" : "false")
      const track = this.manualToggleTarget.querySelector("[data-track]")
      const knob  = this.manualToggleTarget.querySelector("[data-knob]")
      const globe = this.manualToggleTarget.querySelector("[data-globe]")
      if (track) {
        track.classList.toggle("bg-sapphire", this.online)
        track.classList.toggle("bg-slate-200", !this.online)
      }
      if (knob) {
        knob.classList.toggle("translate-x-4", this.online)
      }
      if (globe) {
        globe.classList.toggle("text-sapphire", this.online)
        globe.classList.toggle("text-slate-400", !this.online)
      }
    } else {
      this.manualToggleTarget.classList.add("hidden")
    }
  }

  // Override in subclass for logging (e.g. field_logger)
  _log(_message, _detail) {}

  // Override in subclass to feed ping duration/outcome into a connectivity-
  // level evidence stream (field_network_health.js, ADR-0110). No-op here so
  // this shared base stays framework-agnostic -- apps without a health module
  // (chat) see zero behaviour change from this hook existing.
  _recordPingSample(_durationMs, _outcome) {}
}
